import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Card,
  TabbedComponent,
  TextField,
  TextareaField,
  ToggleSwitchField,
  CodeEditorField,
} from '@dopomogai/ui';
import { Settings, Code, Wrench, Layers } from 'lucide-react';
import type { ToolCreatePayload } from '@dopomogai/types';
import ToolSandboxEnvTab from './ToolSandboxEnvTab';
import ToolOverridesTab from './ToolOverridesTab';

type ToolEditorFormData = ToolCreatePayload & { code: string };

interface ToolEditorTabsProps {
  tool: ToolEditorFormData;
  onFieldChange: (field: keyof ToolEditorFormData, value: any) => void;
  errors?: Partial<Record<keyof ToolEditorFormData, string>>;
  isEditing: boolean;
}

export const ToolEditorTabs: React.FC<ToolEditorTabsProps> = ({
  tool,
  onFieldChange,
  errors = {},
  isEditing,
}) => {
  const [activeTab, setActiveTab] = useState('details');

  const details = (
    <div className="p-6 space-y-6">
      <TextField
        label="Tool Name"
        value={tool.name}
        onChange={(e) => onFieldChange('name', e.target.value)}
        required
        errorText={errors.name}
        placeholder="e.g., Weather Forecast Tool"
      />
      <TextField
        label="Tool Slug (ID)"
        value={tool.slug}
        onChange={(e) => onFieldChange('slug', e.target.value)}
        required
        disabled={isEditing}
        errorText={errors.slug}
        descriptionText="A unique, URL-friendly identifier. Cannot be changed after creation."
        placeholder="e.g., weather-forecast-tool"
      />
      <TextareaField
        label="Description"
        value={tool.description || ''}
        onChange={(e) => onFieldChange('description', e.target.value)}
        required
        rows={4}
        errorText={errors.description}
        descriptionText="This description is injected into the agent’s prompt to help it choose and use this tool correctly."
        placeholder="A tool to get the current weather conditions for a specified city."
      />
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Tool Status</label>
        <div className="flex items-center gap-4">
          <ToggleSwitchField
            label="Tool Status"
            labelPosition="hidden"
            checked={!!tool.enabled}
            onChange={(e) => onFieldChange('enabled', e.target.checked)}
          />
          <div
            className={clsx(
              'font-medium text-sm px-3 py-1 rounded-md border-2 border-black',
              { 'bg-emerald-100 text-emerald-800': tool.enabled, 'bg-slate-100 text-slate-600': !tool.enabled }
            )}
          >
            {tool.enabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>
      </div>
    </div>
  );

  const code = (
    <div className="p-6">
      <CodeEditorField
        label="Python Script"
        value={tool.code}
        onChange={(v) => onFieldChange('code', v || '')}
        language="python"
        height="500px"
      />
    </div>
  );

  // Sandbox: map ENV_VAR_NAME -> ${secret:NAME}
  const sandboxSecrets = useMemo< Record<string, string> >(() => {
    const dp = (tool.default_params || {}) as any;
    const sb = (dp.sandbox || {}) as any;
    return (sb.secrets || {}) as Record<string, string>;
  }, [tool.default_params]);

  const onSandboxChange = (nextMap: Record<string, string>) => {
    const dp = { ...(tool.default_params || {}) } as any;
    dp.sandbox = dp.sandbox || {};
    dp.sandbox.secrets = nextMap;
    onFieldChange('default_params', dp);
  };

  const sandbox = (
    <div className="p-4">
      <ToolSandboxEnvTab value={sandboxSecrets} onChange={onSandboxChange} />
    </div>
  );

  const overrides = (
    <div className="p-4">
      <ToolOverridesTab toolSlug={tool.slug || null} />
    </div>
  );

  const tabDefs = [
    { id: 'details', label: 'Details', icon: <Settings size={16} />, content: details },
    { id: 'code', label: 'Code', icon: <Code size={16} />, content: code },
    { id: 'sandbox', label: 'Sandbox Env', icon: <Wrench size={16} />, content: sandbox },
    { id: 'overrides', label: 'Overrides', icon: <Layers size={16} />, content: overrides },
  ];

  return (
    <Card noPadding>
      <TabbedComponent tabs={tabDefs} activeTabId={activeTab} onTabChange={setActiveTab} />
    </Card>
  );
};

export default ToolEditorTabs;